import React, { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { useCreateFile, useNotifyUploaded } from '../../hooks/useGallery'
import { useQueryClient } from '@tanstack/react-query'
import { useGalleryContext } from '../../context/GalleryContext'
import type { PantryMediaFile } from './types'

const tokens = {
  green: '#2563EB',
  greenH: '#1D4ED8',
  greenLight: '#EFF6FF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray800: '#1F2937',
  red: '#DC2626',
  redBg: '#FEF2F2',
  rSm: 6,
  rMd: 10,
}

const MEDIA_FOLDER_STATIC = {
  ALL_FILE: 'all',
  UNSORTED: 'unsorted',
}

interface UploadedFile extends PantryMediaFile {
  resumable_upload_session?: string
}

export interface PantryUploadZoneProps {
  folderSelectedId: string
  didFinishUploadFiles: (files: UploadedFile[], folderId?: string) => void
  noUploadVideo?: boolean
  uploadMultiFile?: boolean
  acceptTypes?: string
  maximumMbSize?: number
}

export const PantryUploadZone: React.FC<PantryUploadZoneProps> = ({
  folderSelectedId,
  didFinishUploadFiles,
  noUploadVideo = false,
  uploadMultiFile = false,
  acceptTypes = '.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.wmv,.mpg,.ogv,.3gp,.3g2,.webm,.svg',
  maximumMbSize = 10,
}) => {
  const { orgId, getFolderId, compressImage } = useGalleryContext()
  const queryClient = useQueryClient()
  const createFileMutation = useCreateFile()
  const notifyUploadedMutation = useNotifyUploaded()
  const [uploadCount, setUploadCount] = useState(0)
  const [error, setError] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  const folderSelected = MEDIA_FOLDER_STATIC.UNSORTED

  const createFileMedia = (file: File): Promise<UploadedFile> =>
    new Promise(async (resolve, reject) => {
      const normalizedFolderId = folderSelectedId === 'all' || folderSelectedId === 'unsorted' ? '' : folderSelectedId
      const params = {
        parent_id: normalizedFolderId ? getFolderId(normalizedFolderId) : '',
        name: file.name,
        file_type: file.type.split('/')[1],
        content_type: file.type,
        width: 100,
        height: 100,
        size: file.size,
        alternative_text: '',
        title: '',
        caption: '',
        description: '',
        copy_link: null,
        tags: [],
      }
      try {
        const fileResult = await createFileMutation.mutateAsync(params)
        await axios.put(fileResult.resumable_upload_session!, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers':
              'Origin, X-Requested-With, Content-Type, Accept',
            'X-Upload-Content-Type': file.type,
            'X-Upload-Content-Length': file.size,
          },
        })
        const fileInfo = await notifyUploadedMutation.mutateAsync({
          file_id: fileResult._id!,
        })
        resolve(fileInfo as UploadedFile)
      } catch (err) {
        reject(err)
      }
    })

  const handleUploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      setUploadCount(fileArray.length)
      setError('')
      try {
        const compressionOptions = { maxSizeMB: 1, useWebWorker: true }
        const uploadedFiles = await Promise.all(
          fileArray.map(async (file) => {
            const isImage = file.type.split('/')[0] === 'image'
            if (isImage && file.size / 1024 / 1024 > 1 && compressImage) {
              const compressed = await compressImage(file, compressionOptions)
              return createFileMedia(compressed)
            }
            return createFileMedia(file)
          }),
        )
        setUploadCount(0)
        queryClient.invalidateQueries({ queryKey: ['galleries'] })
        queryClient.invalidateQueries({ queryKey: ['pantry-media-images'] })
        const normalizedFolderId = folderSelectedId === 'all' || folderSelectedId === 'unsorted' ? '' : folderSelectedId
        didFinishUploadFiles(
          uploadedFiles.map((f) => ({ ...f, src: f.public_url })),
          normalizedFolderId ? getFolderId(normalizedFolderId) : '',
        )
      } catch {
        setUploadCount(0)
        setError('Upload failed. Please try again.')
      }
    },
    [folderSelectedId, didFinishUploadFiles, createFileMutation, notifyUploadedMutation, getFolderId, compressImage],
  )

  const validateAndUpload = useCallback(
    (files: FileList) => {
      if (!uploadMultiFile && files.length > 1) {
        setError('You can upload only 1 file at a time.')
        return
      }

      const fileArray = Array.from(files)
      let isValid = true

      for (const file of fileArray) {
        const fileSizeMB = file.size / 1024 / 1024
        if (fileSizeMB > maximumMbSize) {
          setError(`File size exceeds ${maximumMbSize}MB limit.`)
          isValid = false
          break
        }

        if (noUploadVideo && file.type.startsWith('video')) {
          setError('Video uploads are not allowed.')
          isValid = false
          break
        }

        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
        if (acceptTypes && !acceptTypes.split(',').includes(fileExtension)) {
          setError(`File type ${fileExtension} is not allowed. Accepted types: ${acceptTypes}`)
          isValid = false
          break
        }
      }

      if (isValid) {
        handleUploadFiles(fileArray)
      }
    },
    [uploadMultiFile, maximumMbSize, noUploadVideo, acceptTypes, handleUploadFiles],
  )

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    validateAndUpload(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {error && (
        <div
          style={{
            padding: '10px 12px',
            background: tokens.redBg,
            border: `1px solid ${tokens.red}`,
            borderRadius: tokens.rSm,
            color: tokens.red,
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}>
            <circle cx="12" cy="12" r="10" />
            <text x="12" y="16" textAnchor="middle" fontSize="12" fill="#fff">!</text>
          </svg>
          {error}
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${isDragging ? tokens.green : tokens.gray300}`,
          borderRadius: tokens.rMd,
          padding: '40px 20px',
          textAlign: 'center',
          background: isDragging ? tokens.greenLight : tokens.gray50,
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={isDragging ? tokens.green : tokens.gray400}
          strokeWidth="1.5"
          style={{ width: 40, height: 40, transition: 'color 0.2s' }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: tokens.gray800, marginBottom: 4 }}>
            Drag and drop files here
          </div>
          <div style={{ fontSize: 12, color: tokens.gray500 }}>
            or click to browse
          </div>
        </div>
        <input
          type="file"
          multiple={uploadMultiFile}
          accept={`${acceptTypes},image/jpeg,image/png,image/gif,image/webp,image/svg+xml,video/mp4,video/x-msvideo,video/quicktime,video/x-ms-wmv,video/mpeg,video/ogg,video/3gpp,video/3gpp2,video/webm`}
          onChange={(e) => {
            if (e.currentTarget.files) {
              validateAndUpload(e.currentTarget.files)
            }
          }}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
          }}
        />
      </div>

      {uploadCount > 0 && (
        <div
          style={{
            padding: '12px 16px',
            background: tokens.greenLight,
            border: `1px solid ${tokens.green}`,
            borderRadius: tokens.rSm,
            color: tokens.green,
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }}
          >
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
          Uploading {uploadCount} file{uploadCount > 1 ? 's' : ''}…
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}

export default PantryUploadZone
